import React, { Fragment, ReactNode } from 'react';
import styled from 'styled-components';

import { WhitePanel } from '../../../../../components/Toolkit/WhitePanel/WhitePanel';
import { MiniUserProfile } from '../../../../../components/Toolkit/MiniUserProfile/MiniUserProfile';
import { VerifiedList } from '../../../../../components/PageWrappers/AdDetails/components/VerifiedList/VerifiedList';
import { Link } from '../../../../../components/Toolkit/Button/Link';

import { media } from '../../../../../helpers/breakpoints';
import { formatRegistrationDate } from '../../../../../helpers/formatting';
import { typeSafeObjectKeys } from '@dist-marketplace-frontend/utils';

export interface SellerInfoPanelProps {
  className?: string;
  title?: string;
  profileImageUrl: string;
  renderSendMessage?: ReactNode;
  renderShowPhone?: ReactNode;
  renderMakeOffer?: ReactNode;
  county?: string;
  type?: string;
  sellerUrl?: string;
  isTrader: boolean;
  verification?: {
    email?: boolean;
    phone?: boolean;
    bank?: boolean;
  };
  registrationDate?: string;
  avgResponseRate?: string;
  adCountStats?: {
    activeAdView?: {
      displayText?: string;
      value?: string;
    };
    lifetimeAdView?: {
      displayText?: string;
      value?: string;
    };
  };
  testId?: string;
}

const SellerInfoPanelWrapper = styled.div`
  display: grid;
  grid-template-columns: auto;

  @media (min-width: 840px) {
    grid-template-columns: repeat(2, 1fr);
    column-gap: ${({ theme }) => theme.spacing.M24};
  }
`;

const SellerDetails = styled.div`
  flex-grow: 1;
  @media (min-width: 840px) {
    grid-row: span 2;
  }
`;

const SellerLink = styled(Link)`
  margin-left: ${({ theme }) => theme.spacing.L56};
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  ${media.medium} {
    margin-bottom: ${({ theme }) => theme.spacing.M24};
  }
`;

const ButtonList = styled.ul<{ hasTwoButtons: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.S8};
`;

const AdCountStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, auto);
  justify-content: space-between;
  padding-bottom: ${({ theme }) => theme.spacing.M16};
  border-bottom: 1px solid ${({ theme }) => theme.colors.GREY_LIGHT};
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  @media (min-width: 840px) {
    order: 3;
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const StatsKey = styled.p`
  ${({ theme }) => theme.fontSize.M14}
`;

const StatsValue = styled.p`
  ${({ theme }) => theme.fontSize.M14}
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  text-align: right;
`;

const CommunitySection = styled.div`
  @media (min-width: 840px) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.GREY_LIGHT};
    margin-bottom: ${({ theme }) => theme.spacing.M16};
  }
`;

const Title = styled.p`
  ${({ theme }) => theme.fontSize.M16}
  color: ${({ theme }) => theme.colors.GREY_DARKER};
`;

const Subtitle = styled.p`
  ${({ theme }) => theme.fontSize.M14}
`;

const LearnMore = styled(Link)`
  @media (min-width: 840px) {
    margin-bottom: ${({ theme }) => theme.spacing.M16};
  }
`;

const SVerifiedList = styled(VerifiedList)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

function SellerInfoPanel({
  profileImageUrl,
  title,
  renderSendMessage,
  renderShowPhone,
  renderMakeOffer,
  county,
  type,
  sellerUrl,
  isTrader,
  verification,
  registrationDate,
  avgResponseRate,
  adCountStats,
  className,
  testId,
}: SellerInfoPanelProps) {
  return (
    <WhitePanel className={className} testId={testId}>
      <SellerInfoPanelWrapper>
        <SellerDetails>
          {title && (
            <MiniUserProfile
              title={title}
              illustration={profileImageUrl}
              county={county}
              type={type}
              isTrader={isTrader}
              verification={verification}
            />
          )}
          {sellerUrl && (
            <SellerLink
              href={sellerUrl}
              ofType="SECONDARY"
              data-tracking="view-all-ads-link"
              rel="nofollow"
              underline
            >
              {isTrader ? 'View Our Shop' : 'View All Ads'}
            </SellerLink>
          )}
          {verification && (
            <SVerifiedList layout="TWO_COLUMN" verification={verification} />
          )}
          <ButtonList
            hasTwoButtons={Boolean(renderSendMessage && renderShowPhone)}
          >
            {renderMakeOffer && <li>{renderMakeOffer}</li>}
            {renderSendMessage && <li>{renderSendMessage}</li>}
            {renderShowPhone && <li>{renderShowPhone}</li>}
          </ButtonList>
        </SellerDetails>
        <AdCountStats>
          <StatsKey>Avg. Response Rate</StatsKey>
          <StatsValue>{avgResponseRate}</StatsValue>
          <StatsKey>Location</StatsKey>
          <StatsValue>{county}</StatsValue>
          {registrationDate && (
            <>
              <StatsKey>Donedealing since</StatsKey>
              <StatsValue>
                {formatRegistrationDate(registrationDate)}
              </StatsValue>
            </>
          )}
          {adCountStats &&
            typeSafeObjectKeys(adCountStats).map(
              (stats) =>
                stats && (
                  <Fragment key={stats}>
                    <StatsKey>{adCountStats[stats]!.displayText}</StatsKey>
                    <StatsValue>{adCountStats[stats]!.value}</StatsValue>
                  </Fragment>
                ),
            )}
        </AdCountStats>
        <CommunitySection>
          <Title>Help build a safer community</Title>
          <Subtitle>Our commitment towards building trust</Subtitle>
          <LearnMore
            href="https://www.assist.donedeal.ie/verified-profiles"
            ofType="SECONDARY"
            underline
            fontSize="M14"
          >
            Learn More
          </LearnMore>
        </CommunitySection>
      </SellerInfoPanelWrapper>
    </WhitePanel>
  );
}

export { SellerInfoPanel };
